import {
  FETCH_STATUS_FAILED,
  FETCH_STATUS_NONE,
  FETCH_STATUS_PENDING,
  FETCH_STATUS_SUCCESS,
  FetchStatus,
} from '@neptune/shared/common-domain';
import { Entity, isEntityEquals } from '@neptune/shared/entity-domain';
import { makeEntityIdentifier } from '@neptune/shared/entity-util';

import { getNameFromAttributes, knownAttributes } from 'domain/experiment/attribute';

import {
  CurrentBasicEntityActionTypes,
  CurrentEntityActions,
  UpdateCurrentBasicEntityActionTypes,
} from './actions';

type BasicEntityState = {
  fetchStatus: FetchStatus;
  entity?: Entity;
  error: unknown | null;
  requestedId: string;
  receivedId: string;
};

export type CurrentEntityState = {
  basicEntity: BasicEntityState;
};

const initialState: CurrentEntityState = {
  basicEntity: {
    fetchStatus: FETCH_STATUS_NONE,
    entity: undefined,
    error: null,
    requestedId: '',
    receivedId: '',
  },
};

export function currentEntityReducer(
  state: CurrentEntityState = initialState,
  action: CurrentEntityActions,
): CurrentEntityState {
  switch (action.type) {
    case CurrentBasicEntityActionTypes.request: {
      const requestedId = makeEntityIdentifier(
        action.payload.organizationName,
        action.payload.projectName,
        action.payload.runIdentificationKey,
      );
      return {
        ...state,
        basicEntity: {
          ...state.basicEntity,
          fetchStatus: FETCH_STATUS_PENDING,
          requestedId,
          error: null,
        },
      };
    }

    case CurrentBasicEntityActionTypes.fail: {
      return {
        ...state,
        basicEntity: {
          ...state.basicEntity,
          fetchStatus: FETCH_STATUS_FAILED,
          error: action.error,
          entity: undefined,
        },
      };
    }

    case CurrentBasicEntityActionTypes.success: {
      return {
        ...state,
        basicEntity: {
          ...state.basicEntity,
          fetchStatus: FETCH_STATUS_SUCCESS,
          entity: action.payload,
          error: null,
        },
      };
    }

    case UpdateCurrentBasicEntityActionTypes.success: {
      if (!state.basicEntity.entity) {
        return state;
      }

      const prevExperimentName = getNameFromAttributes(state.basicEntity.entity.attributes);
      const experimentName = getNameFromAttributes(action.payload.attributes);

      const prevId = state.basicEntity.entity?.id;
      const id = action.payload.id;

      if (
        (prevExperimentName && experimentName !== prevExperimentName) ||
        id !== prevId ||
        (makeSureEntityIsSmallEnoughForDeepCompare(state.basicEntity.entity) &&
          isEntityEquals(action.payload, state.basicEntity.entity))
      ) {
        return state;
      }

      return {
        ...state,
        basicEntity: {
          ...state.basicEntity,
          entity: action.payload,
          error: null,
        },
      };
    }

    default:
      return state;
  }
}

function makeSureEntityIsSmallEnoughForDeepCompare(entity: Entity) {
  return entity.attributes.length < 2 * knownAttributes.length;
}
